import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import AuthPage from "@/pages/AuthPage";
import NotesPage from "@/pages/NotesPage";
import NoteEditor from "@/pages/NoteEditor";
import PolishPage from "@/pages/PolishPage";
import ClientsPage from "@/pages/ClientsPage";
import PrintNote from "@/pages/PrintNote";
import ProgressPage from "@/pages/ProgressPage";
import { IdleGuard } from "@/components/IdleGuard";
import BillingPage from "@/pages/BillingPage";
import PaymentResult from "@/pages/PaymentResult";
import InvitePage from "@/pages/InvitePage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordReset";

const UNGATED = ["/billing", "/payment"];

function Protected({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null)
    return (
      <div className="min-h-screen grid place-items-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (!user.access?.has_access && !UNGATED.some((p) => pathname.startsWith(p))) return <Navigate to="/billing" replace />;
  return <>{children}<IdleGuard /></>;
}

function NewNoteRoute() {
  return <NoteEditor key={useLocation().key} />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const params = new URLSearchParams(useLocation().search);
  const invite = params.get("invite");
  if (user) {
    const next = params.get("next");
    return <Navigate to={invite ? `/invite/${invite}` : next && next.startsWith("/") && !next.startsWith("//") ? next : "/"} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><AuthPage /></PublicOnly>} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/notes/:id/print" element={<Protected><PrintNote /></Protected>} />
          <Route path="/" element={<Protected><AppLayout /></Protected>}>
            <Route index element={<NotesPage />} />
            <Route path="notes/new" element={<NewNoteRoute />} />
            <Route path="notes/:id" element={<NoteEditor />} />
            <Route path="polish" element={<PolishPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="clients/:id/progress" element={<ProgressPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="payment/success" element={<PaymentResult success />} />
            <Route path="payment/cancel" element={<PaymentResult />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}

export default App;
